import { useIntl } from 'react-intl';

import { Alert, Box, Button, Typography } from '@evaneos/b2b-ui';
import RefreshIcon from '@evaneos/b2b-ui/icons/Refresh';

import ReactMarkdown from 'react-markdown';

type SummaryTabProps = {
    summary: string | null;
    hasSummaryError: boolean;
    shouldShowTranscriptError: boolean;
    canRegenerate: boolean;
    isRegenerating: boolean;
    onRegenerate: () => void;
};

export const SummaryTab = ({
    summary,
    hasSummaryError,
    shouldShowTranscriptError,
    canRegenerate,
    isRegenerating,
    onRegenerate,
}: SummaryTabProps) => {
    const { formatMessage } = useIntl();

    return (
        <div>
            {hasSummaryError && (
                <Alert
                    severity="error"
                    sx={{ mb: 2, border: '1px solid', borderColor: 'error.dark' }}
                >
                    <Typography
                        variant="body2"
                        sx={{ color: 'inherit', fontWeight: 'bold', marginBottom: 0.5 }}
                    >
                        {formatMessage({
                            id: 'dossier.discussion.phone_call.summary.error.not_available.title',
                        })}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'inherit', marginBottom: 1.5 }}>
                        {formatMessage({
                            id: 'dossier.discussion.phone_call.summary.error.not_available.message',
                        })}
                    </Typography>
                    {canRegenerate && (
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<RefreshIcon />}
                            onClick={onRegenerate}
                            disabled={isRegenerating}
                        >
                            {formatMessage({
                                id: 'dossier.discussion.phone_call.regeneration.summary.button',
                            })}
                        </Button>
                    )}
                </Alert>
            )}
            {!hasSummaryError && shouldShowTranscriptError && (
                <Alert
                    severity="error"
                    sx={{ mb: 2, border: '1px solid', borderColor: 'error.dark' }}
                >
                    <Typography
                        variant="body2"
                        sx={{ color: 'inherit', fontWeight: 'bold', marginBottom: 0.5 }}
                    >
                        {formatMessage({
                            id: 'dossier.discussion.phone_call.summary.error.not_available.title',
                        })}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'inherit', marginBottom: 1.5 }}>
                        {formatMessage({
                            id: 'dossier.discussion.phone_call.transcript.error.not_available.message',
                        })}
                    </Typography>
                    {canRegenerate && (
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<RefreshIcon />}
                            onClick={onRegenerate}
                            disabled={isRegenerating}
                        >
                            {formatMessage({
                                id: 'dossier.discussion.phone_call.notification.retry_generation',
                            })}
                        </Button>
                    )}
                </Alert>
            )}
            {summary && (
                <Box>
                    <ReactMarkdown
                        components={{
                            ul: ({ children, ...props }) => (
                                <Typography
                                    component="ul"
                                    sx={{
                                        paddingLeft: 0,
                                        '& ul': {
                                            paddingLeft: '1.5rem',
                                            '& li': {
                                                listStyle: 'circle inside',
                                                marginLeft: '0.5rem',
                                            },
                                        },
                                    }}
                                    {...props}
                                >
                                    {children}
                                </Typography>
                            ),
                            li: ({ children, ...props }) => (
                                <Typography
                                    component="li"
                                    variant="body2"
                                    sx={{ listStyle: 'inside' }}
                                    {...props}
                                >
                                    {children}
                                </Typography>
                            ),
                            strong: ({ children }) => (
                                <Typography component="strong" variant="body2" fontWeight="bold">
                                    {children}
                                </Typography>
                            ),
                            b: ({ children }) => (
                                <Typography component="strong" variant="body2" fontWeight="bold">
                                    {children}
                                </Typography>
                            ),
                            em: ({ children }) => (
                                <Typography component="em" variant="body2" fontStyle="italic">
                                    {children}
                                </Typography>
                            ),
                            i: ({ children }) => (
                                <Typography component="em" variant="body2" fontStyle="italic">
                                    {children}
                                </Typography>
                            ),
                            p: ({ children }) => (
                                <Typography variant="body2">{children}</Typography>
                            ),
                            h1: ({ children }) => <Typography variant="h1">{children}</Typography>,
                            h2: ({ children }) => <Typography variant="h2">{children}</Typography>,
                            h3: ({ children }) => <Typography variant="h3">{children}</Typography>,
                            h4: ({ children }) => <Typography variant="h4">{children}</Typography>,
                        }}
                    >
                        {summary}
                    </ReactMarkdown>
                    {!hasSummaryError && canRegenerate && (
                        <Box
                            sx={{
                                marginTop: 2,
                                borderTop: '1px solid',
                                borderColor: 'border.main',
                                paddingTop: 2,
                            }}
                        >
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<RefreshIcon />}
                                onClick={onRegenerate}
                                disabled={isRegenerating}
                            >
                                {formatMessage({
                                    id: 'dossier.discussion.phone_call.regeneration.summary.button',
                                })}
                            </Button>
                        </Box>
                    )}
                </Box>
            )}
        </div>
    );
};
